import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { getOrderMetas, ItemMeta } from './OrderMetaHelper';

// Set by the marketplace worker when the buyer is a business. The value names the programme.
export const BusinessOrderMetaKey = 'BusinessOrder';
export const AmazonBusinessProgram = 'AMAZON_BUSINESS';

// Set by the marketplace worker when the buyer has told the marketplace when they can take deliveries.
// JSON in Amazon's PreferredDeliveryTime shape, kept as-is by the worker.
export const PreferredDeliveryTimeMetaKey = 'PreferredDeliveryTime';

// Stamped by the Amazon worker on every orderline, naming the Amazon site the order was sold on.
export const MarketplaceIdMetaKey = 'MarketplaceId';

const BusinessHourDeliveryHelpPath = '/help/hub/reference/GL6AWZ3XEHAQ66M8';
const SellerCentralEurope = { site: 'Europe', url: 'https://sellercentral-europe.amazon.com' };

// The Seller Central site for each Amazon marketplace, so the policy link opens the seller's own help pages - this
// app runs on tenants outside the UK. Ids are the worker's (Regions.cs in worker-marketplaces), sites are the ones
// edge-account-linking sends sellers to for the same countries (Amazon:Regions in its appsettings.json).
const SellerCentralByMarketplaceId: { [marketplaceId: string]: { site: string; url: string } } = {
  ATVPDKIKX0DER: { site: 'US', url: 'https://sellercentral.amazon.com' },
  A2EUQ1WTGCTBG2: { site: 'Canada', url: 'https://sellercentral.amazon.ca' },
  A1AM78C64UM0Y8: { site: 'Mexico', url: 'https://sellercentral.amazon.com.mx' },
  A2Q3Y263D00KWC: { site: 'Brazil', url: 'https://sellercentral.amazon.com.br' },
  A1F83G8C2ARO7P: SellerCentralEurope, // UK
  A1RKKUPIHCS9HS: SellerCentralEurope, // Spain
  A13V1IB3VIYZZH: SellerCentralEurope, // France
  A1PA6795UKMFR9: SellerCentralEurope, // Germany
  APJ6JRA9NG5V4: SellerCentralEurope, // Italy
  A28R8C7NBKEWEA: { site: 'Ireland', url: 'https://sellercentral.amazon.ie' },
  AMEN7PMS3EDWL: { site: 'Belgium', url: 'https://sellercentral.amazon.com.be' },
  A1805IZSGTT6HS: { site: 'Netherlands', url: 'https://sellercentral.amazon.nl' },
  A2NODRKZP88ZB9: { site: 'Sweden', url: 'https://sellercentral.amazon.se' },
  AE08WJ6YKNBMC: { site: 'South Africa', url: 'https://sellercentral.amazon.co.za' },
  A1C3SOZRARQ6R3: { site: 'Poland', url: 'https://sellercentral.amazon.pl' },
  ARBP9OOSHTCHU: { site: 'Egypt', url: 'https://sellercentral.amazon.eg' },
  A33AVAJ2PDY3EV: { site: 'Turkey', url: 'https://sellercentral.amazon.com.tr' },
  A17E79C6D8DWNP: { site: 'Saudi Arabia', url: 'https://sellercentral.amazon.sa' },
  A2VIGQ35RCS4UG: { site: 'U.A.E.', url: 'https://sellercentral.amazon.ae' },
  A21TJRUUN4KGV: { site: 'India', url: 'https://sellercentral.amazon.in' },
  A19VAU5U5O7RUS: { site: 'Singapore', url: 'https://sellercentral.amazon.sg' },
  A39IBJ37TRP1C6: { site: 'Australia', url: 'https://sellercentral.amazon.com.au' },
  A1VC38T7YXB528: { site: 'Japan', url: 'https://sellercentral.amazon.co.jp' }
};

export type PolicyLink = { site: string; url: string };

// The Business Hour Delivery Rate help page on the Seller Central site for the marketplace, or undefined for one we
// do not know - better no link than the wrong country's.
export const getBusinessHourDeliveryPolicy = (marketplaceId?: string): PolicyLink | undefined => {
  const sellerCentral = marketplaceId ? SellerCentralByMarketplaceId[marketplaceId] : undefined;
  return sellerCentral
    ? { site: sellerCentral.site, url: sellerCentral.url + BusinessHourDeliveryHelpPath }
    : undefined;
};

type TimeOfDay = { Hour?: number; Minute?: number };
type OpenInterval = { StartTime?: TimeOfDay; EndTime?: TimeOfDay };

export type PreferredDeliveryTime = {
  BusinessHours?: { DayOfWeek?: string; OpenIntervals?: OpenInterval[] }[];
  ExceptionDates?: { ExceptionDate?: string; IsOpen?: boolean; OpenIntervals?: OpenInterval[] }[];
};

export type OpeningHoursRow = { days: string; hours: string };

export type EstimatedDelivery = {
  // eg "Fri 2 Oct", or "Fri 2 Oct - Mon 5 Oct" for a window.
  days: string;
  dayCount: number;
  // Days in the window the business is closed, eg ["Sat 3 Oct", "Sun 4 Oct"]. Empty when open throughout.
  closedDays: string[];
  // Every day in the window is a day they are closed.
  allClosed: boolean;
  // False when the buyer has set no hours, so whether they are open is unknown.
  checked: boolean;
};

export type BusinessDelivery = {
  reference: string;
  name?: string;
  // Empty when the buyer has not set opening hours.
  openingHours: OpeningHoursRow[];
  // Upcoming closures or changed hours. Amazon only sends the next 30 days.
  exceptions: OpeningHoursRow[];
  // The quoted delivery window checked against the hours, when we can trust it for this booking.
  estimatedDelivery?: EstimatedDelivery;
  // The policy on the Seller Central site the order was sold through.
  policy?: PolicyLink;
};

// A zoneless calendar day, yyyy-MM-dd.
type CalendarDay = string;

// Longest delivery window we will spell out day by day.
const MaxWindowDays = 14;

const Days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DayNames: { [key: string]: string } = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun'
};

const isBusinessMeta = (meta?: ItemMeta) => !!meta?.[BusinessOrderMetaKey]?.Value;

export const parsePreferredDeliveryTime = (value?: string): PreferredDeliveryTime | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const pad = (n?: number) => String(n ?? 0).padStart(2, '0');

// Calendar days follow ui-checkout's docs/DATES.md. A wire value is read from its leading yyyy-MM-dd, never through
// new Date() - its offset is the API host's, so converting it moves the day for anyone west of that host. A value
// this UI has already turned into a Date is read as the local day, which is the day the seller was shown for it.
export const toCalendarDay = (value?: Date | string | null): CalendarDay | undefined => {
  if (typeof value === 'string') {
    return /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0];
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return undefined;
};

// UTC-anchored and only ever read back in UTC, so the day cannot move with the viewer's zone.
const anchor = (day: CalendarDay) => new Date(`${day}T00:00:00Z`);
const addDays = (day: CalendarDay, n: number) =>
  new Date(anchor(day).getTime() + n * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);
const dayOfWeek = (day: CalendarDay) => Days[(anchor(day).getUTCDay() + 6) % 7];
const formatDay = (day: CalendarDay) =>
  anchor(day)
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .replace(',', '');
const formatTime = (t?: TimeOfDay) => `${pad(t?.Hour)}:${pad(t?.Minute)}`;
const formatIntervals = (intervals?: OpenInterval[]) =>
  intervals && intervals.length > 0
    ? intervals.map((i) => `${formatTime(i.StartTime)} - ${formatTime(i.EndTime)}`).join(', ')
    : undefined;

// One row per run of consecutive days with the same hours, Monday first, eg "Mon - Fri" / "09:00 - 17:00".
// A day Amazon does not list is one the business is not open for deliveries.
export const summariseOpeningHours = (preferred?: PreferredDeliveryTime): OpeningHoursRow[] => {
  const businessHours = preferred?.BusinessHours ?? [];
  if (businessHours.length === 0) {
    return [];
  }

  const hoursByDay = Days.map((day) => {
    const intervals = businessHours
      .filter((x) => x.DayOfWeek?.toUpperCase() === day)
      .flatMap((x) => x.OpenIntervals ?? []);
    return { day, hours: formatIntervals(intervals) ?? 'Closed' };
  });

  const rows: { from: string; to: string; hours: string }[] = [];
  hoursByDay.forEach((x) => {
    const last = rows[rows.length - 1];
    if (last && last.hours === x.hours) {
      last.to = x.day;
    } else {
      rows.push({ from: x.day, to: x.day, hours: x.hours });
    }
  });

  return rows.map((x) => ({
    days: x.from === x.to ? DayNames[x.from] : `${DayNames[x.from]} - ${DayNames[x.to]}`,
    hours: x.hours
  }));
};

// Closures and changed hours from today onwards, soonest first, eg "Mon 25 Aug" / "Closed".
export const summariseExceptions = (preferred: PreferredDeliveryTime | undefined, today: Date): OpeningHoursRow[] => {
  const todayDay = toCalendarDay(today)!;
  return (preferred?.ExceptionDates ?? [])
    .map((x) => ({ ...x, day: toCalendarDay(x.ExceptionDate) }))
    .filter((x): x is typeof x & { day: CalendarDay } => !!x.day && x.day >= todayDay)
    .sort((a, b) => (a.day > b.day ? 1 : -1))
    .map((x) => ({
      days: formatDay(x.day),
      hours: x.IsOpen ? (formatIntervals(x.OpenIntervals) ?? 'Open') : 'Closed'
    }));
};

// Whether the business takes deliveries on the day, or undefined when they have not said. A closure or
// changed hours on that date beats the weekly pattern, and a weekday Amazon does not list is a closed one.
export const isOpenOn = (preferred: PreferredDeliveryTime | undefined, day: CalendarDay): boolean | undefined => {
  const exception = preferred?.ExceptionDates?.find((x) => toCalendarDay(x.ExceptionDate) === day);
  if (exception) {
    return !!exception.IsOpen;
  }

  const businessHours = preferred?.BusinessHours ?? [];
  if (businessHours.length === 0) {
    return undefined;
  }

  return businessHours.some((x) => x.DayOfWeek?.toUpperCase() === dayOfWeek(day) && (x.OpenIntervals?.length ?? 0) > 0);
};

// The delivery window of the quote being booked, checked against the opening hours - the SmartSend twin of
// ui-checkout's estimatedDeliveryCalendarDateMax. A quote's window only holds for the collection day it was priced
// for, so if the item's collection day has moved on since, say nothing rather than show another booking's window.
export const getEstimatedDelivery = (
  item: SmartSendItemApiModel,
  preferred: PreferredDeliveryTime | undefined
): EstimatedDelivery | undefined => {
  const option = item.State?.validation?.QuoteState?.Quote?.Options?.[0];
  const latest = toCalendarDay(option?.EstimatedDeliveryDate);
  if (!latest) {
    return undefined;
  }

  const quotedCollection = toCalendarDay(option?.Collection);
  const collection = toCalendarDay(item.PreferredCollectionDate);
  if (quotedCollection && collection && quotedCollection !== collection) {
    return undefined;
  }

  const earliest = toCalendarDay(option?.EstimatedDeliveryDateMin);
  const first = earliest && earliest < latest ? earliest : latest;

  const window: CalendarDay[] = [];
  for (let day = first; day <= latest && window.length < MaxWindowDays; day = addDays(day, 1)) {
    window.push(day);
  }

  const days = window.map((day) => ({ day, open: isOpenOn(preferred, day) }));
  const closed = days.filter((x) => x.open === false);

  return {
    days: first === latest ? formatDay(latest) : `${formatDay(first)} - ${formatDay(latest)}`,
    dayCount: days.length,
    closedDays: closed.map((x) => formatDay(x.day)),
    allClosed: closed.length === days.length,
    checked: days.some((x) => x.open !== undefined)
  };
};

const toList = (values: string[]) =>
  values.length > 1 ? `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}` : values.join('');

// What to tell the seller about the window, or undefined when there are no hours to check it against.
export const describeEstimatedDelivery = (
  estimate: EstimatedDelivery
): { text: string; closed: boolean } | undefined => {
  if (!estimate.checked) {
    return undefined;
  }
  if (estimate.allClosed) {
    return {
      text: estimate.dayCount === 1 ? 'they’re closed that day.' : 'they’re closed on all of those days.',
      closed: true
    };
  }
  if (estimate.closedDays.length > 0) {
    return { text: `they’re closed on ${toList(estimate.closedDays)}.`, closed: true };
  }
  return {
    text: estimate.dayCount === 1 ? 'they’re open that day.' : 'they’re open on all of those days.',
    closed: false
  };
};

export const getPolicyLinks = (deliveries: BusinessDelivery[]): PolicyLink[] =>
  deliveries
    .map((x) => x.policy)
    .filter((x): x is PolicyLink => !!x)
    .filter((x, i, all) => all.findIndex((y) => y.url === x.url) === i);

const hasHours = (x: BusinessDelivery) => x.openingHours.length > 0 || x.exceptions.length > 0;

// One entry per business order, including each business order inside a grouped parcel, and never the same
// order twice - a single order is found on both the item and its orderline, and a split parcel repeats it.
export const getBusinessDeliveries = (
  items: SmartSendItemApiModel[],
  getReference: (item: SmartSendItemApiModel) => string | undefined,
  today: Date = new Date()
): BusinessDelivery[] => {
  const deliveries: BusinessDelivery[] = [];
  items.forEach((item) => {
    getOrderMetas(item, getReference)
      .filter((x) => isBusinessMeta(x.meta))
      .forEach(({ reference, meta }) => {
        const preferred = parsePreferredDeliveryTime(meta?.[PreferredDeliveryTimeMetaKey]?.Value);
        const delivery: BusinessDelivery = {
          reference,
          name: item.Recipient?.Name,
          openingHours: summariseOpeningHours(preferred),
          exceptions: summariseExceptions(preferred, today),
          estimatedDelivery: getEstimatedDelivery(item, preferred),
          policy: getBusinessHourDeliveryPolicy(meta?.[MarketplaceIdMetaKey]?.Value)
        };

        // Keep whichever copy of the order carries its hours, and a policy link from either copy.
        const existing = deliveries.findIndex((x) => x.reference === reference);
        if (existing === -1) {
          deliveries.push(delivery);
        } else {
          if (!hasHours(deliveries[existing]) && hasHours(delivery)) {
            deliveries[existing] = { ...delivery, policy: delivery.policy ?? deliveries[existing].policy };
          }
          deliveries[existing].policy ??= delivery.policy;
        }
      });
  });
  return deliveries;
};
